import React, { useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { kitPictureSrc } from '../kitPictureSrc';
import { platformMeta } from '../platformMeta';
import { isSocialPlatform } from '../types';
import { PostsBoardFile, postFileLabel, postFileThumbPicture, webAssetOf, webReelOf } from './postsModel';
import { postsMessages } from './postsMessages';
import { WatermarkFlag } from '../../watermarkUpsell/files/WatermarkFlag';
import { fileHasWatermark } from '../../watermarkUpsell/files/watermarkedFile';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A long press (c3) picks the file on a phone. */
const LONG_PRESS_MS = 500;

/** The CSS size of the row thumbnail (Posts.module.scss `.thumb`). */
const THUMB_PX = 52;

interface PostsFileRowProps {
  file: PostsBoardFile;
  /** The second line: the song, the time or the reason. */
  detail: string;
  platforms?: string[];
  /** The right side: a chip, a time, the numbers and the actions. */
  right?: React.ReactNode;
  failed?: boolean;
  /** Select mode: the row shows a checkbox and a click picks it. Null: the file cannot be picked. */
  selection: { isOn: boolean; isPicked: boolean; toggle: () => void } | null;
  onOpen: (file: PostsBoardFile) => void;
}

/**
 * One file in a lane (design assets-rethink c1 `.pk`): the thumb, the label, the detail line, the
 * channel icons, and the right side. In Select mode a click picks the file; else it opens the
 * viewer. A long press starts Select mode (c3).
 */
const PostsFileRowComponent: React.FC<PostsFileRowProps> = (props) => {
  const { file, detail, platforms, right, failed = false, selection, onOpen } = props;
  const intl = useIntl();
  const label = postFileLabel(intl, file);
  const thumb = postFileThumbPicture(file);
  const hasWatermark = file.state === 'ready' && fileHasWatermark(webAssetOf(file) ?? webReelOf(file));
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const isSelecting = selection !== null && selection.isOn;

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const startPress = useCallback(() => {
    longPressed.current = false;
    if (selection === null) {
      return;
    }
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      selection.toggle();
    }, LONG_PRESS_MS);
  }, [selection]);
  const click = useCallback(() => {
    clearTimer();
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    if (isSelecting && selection !== null) {
      selection.toggle();
      return;
    }
    onOpen(file);
  }, [clearTimer, file, isSelecting, onOpen, selection]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        styles.row,
        isSelecting && styles.rowSelecting,
        selection?.isPicked === true && styles.rowPicked,
        failed && styles.rowFailed
      )}
      onClick={click}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          click();
        }
      }}
      onTouchStart={startPress}
      onTouchEnd={clearTimer}
      onTouchMove={clearTimer}
      data-testid="posts-row"
    >
      {isSelecting && selection !== null && (
        <input
          type="checkbox"
          className="form-check-input m-0"
          checked={selection.isPicked}
          aria-label={intl.formatMessage(postsMessages.pickFile, { name: label })}
          onChange={selection.toggle}
          onClick={(event) => event.stopPropagation()}
        />
      )}
      {thumb === null ? (
        <span className={styles.thumb} />
      ) : (
        <img className={styles.thumb} src={kitPictureSrc(thumb, THUMB_PX)} alt="" loading="lazy" />
      )}
      <div className={styles.rowText}>
        <b>{label}</b>
        {/* design: trial-upsell a4. A ready file with the Music24 watermark says so. The flag has its
            own line, so it never pushes the title into an ellipsis (QA 2026-10-28, mob-21). */}
        {hasWatermark && <WatermarkFlag inline className={styles.rowFlag} />}
        <span>{detail}</span>
        {platforms !== undefined && platforms.length > 0 && (
          <span className={styles.channels}>
            {platforms.filter(isSocialPlatform).map((platform) => (
              <FontAwesomeIcon key={platform} icon={platformMeta[platform].icon} title={platformMeta[platform].name} />
            ))}
          </span>
        )}
      </div>
      {/* The actions do not open the viewer. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.rowRight} onClick={(event) => event.stopPropagation()}>
        {selection?.isPicked === true && <FontAwesomeIcon icon={faCheck} className="text-primary" />}
        {right}
      </div>
    </div>
  );
};

export const PostsFileRow = TypedMemo(PostsFileRowComponent);
